/**
 * screens/components/MergeReviewDialog.js
 *
 * The "review & approve" gate for the git-style merge/restore flow.
 *
 * Yjs auto-merges with no true conflicts, so there are no raw conflict markers
 * to resolve.  Instead we present a human-friendly diff:
 *   - added events (green)   — what comes IN with this merge/restore
 *   - removed events (red)   — what goes away
 *   - possible duplicates    — the same event tagged on both sides; the user
 *                              ticks the ones to de-duplicate (default: all)
 *
 * On approve we call MergeManager.applyMerge (which commits the result and
 * repoints the live Yjs doc) and hand the merged string back to the parent so
 * it can dispatch it into Redux — the editor then continues from the merge.
 *
 * Styling mirrors PreviewDialog so it feels native to the History screen.
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { CallMerge, History as HistoryIcon, CheckCircleOutline } from "@mui/icons-material";
import toast from "react-hot-toast";
import { applyMerge } from "../../persistence/vcs/MergeManager";

const MergeReviewDialog = ({ preview, onClose, onMerged }) => {
  // Default: de-duplicate every flagged duplicate.
  const [dedupe, setDedupe] = useState(() =>
    new Set((preview?.possibleDuplicates ?? []).map((d) => d.token))
  );
  const [applying, setApplying] = useState(false);

  const isRestore = preview?.mode === "restore";
  const added = preview?.addedTokens ?? [];
  const removed = preview?.removedTokens ?? [];
  const dupes = preview?.possibleDuplicates ?? [];

  const noChange = added.length === 0 && removed.length === 0;

  const toggleDupe = useCallback((token) => {
    setDedupe((prev) => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  }, []);

  const handleApprove = useCallback(async () => {
    setApplying(true);
    try {
      const { string } = await applyMerge({
        preview,
        dedupeTokens: Array.from(dedupe),
      });
      toast.success(isRestore ? "Version restored ✓" : "Work combined ✓", { duration: 3000 });
      if (onMerged) onMerged(string);
    } catch (err) {
      toast.error((isRestore ? "Restore" : "Combine") + " failed: " + err.message, { duration: 4000 });
    } finally {
      setApplying(false);
    }
  }, [preview, dedupe, isRestore, onMerged]);

  const title = isRestore ? "Review restore" : "Review combine";
  const Icon = isRestore ? HistoryIcon : CallMerge;

  const summary = useMemo(
    () => `${added.length} added · ${removed.length} removed`,
    [added.length, removed.length]
  );

  if (!preview) return null;

  return (
    <Dialog
      open
      onClose={applying ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: "var(--surface-1)",
          color: "var(--text-1)",
          border: "1px solid var(--border-1)",
        },
      }}
    >
      <DialogTitle sx={{ py: 2, px: 3, borderBottom: "1px solid var(--border-1)" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon sx={{ fontSize: 18, color: "var(--text-2)" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontFamily="Poppins" fontWeight={700} color="var(--text-1)">
              {title}
            </Typography>
            <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
              {isRestore ? "Restoring " : "Combining "}
              <strong>{preview.sourceLabel}</strong>
              {" → "}
              {preview.targetBranchName} · {summary}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {noChange && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="var(--text-2)" fontFamily="Poppins">
              No differences — the result is identical to your current branch.
            </Typography>
          </Box>
        )}

        {/* Added */}
        {added.length > 0 && (
          <Box sx={{ p: 2.5, borderBottom: "1px solid var(--border-1)" }}>
            <Typography variant="overline" sx={{ color: "#22c55e", display: "block", mb: 1, fontSize: 10 }}>
              + Added ({added.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 180, overflowY: "auto" }}>
              {added.map((t, i) => (
                <Chip
                  key={i}
                  label={t}
                  size="small"
                  sx={{ height: 22, fontFamily: "monospace", fontSize: 11,
                        bgcolor: "rgba(34,197,94,0.12)", color: "#16a34a",
                        border: "1px solid rgba(34,197,94,0.4)" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Removed */}
        {removed.length > 0 && (
          <Box sx={{ p: 2.5, borderBottom: "1px solid var(--border-1)" }}>
            <Typography variant="overline" sx={{ color: "#ef4444", display: "block", mb: 1, fontSize: 10 }}>
              − Removed ({removed.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 140, overflowY: "auto" }}>
              {removed.map((t, i) => (
                <Chip
                  key={i}
                  label={t}
                  size="small"
                  sx={{ height: 22, fontFamily: "monospace", fontSize: 11,
                        bgcolor: "rgba(239,68,68,0.12)", color: "#dc2626",
                        border: "1px solid rgba(239,68,68,0.4)",
                        textDecoration: "line-through" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Possible duplicates (merge mode only) */}
        {dupes.length > 0 && (
          <Box sx={{ p: 2.5 }}>
            <Typography variant="overline" sx={{ color: "#f59e0b", display: "block", mb: 0.5, fontSize: 10 }}>
              Possible duplicates ({dupes.length})
            </Typography>
            <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins" sx={{ display: "block", mb: 1 }}>
              The same event appears on both sides. Tick to keep a single copy.
            </Typography>
            <Stack spacing={0.25}>
              {dupes.map((d) => (
                <FormControlLabel
                  key={d.token}
                  control={
                    <Checkbox
                      size="small"
                      checked={dedupe.has(d.token)}
                      onChange={() => toggleDupe(d.token)}
                    />
                  }
                  label={
                    <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: 11 }}>
                      {d.token}{" "}
                      <Typography component="span" variant="caption" color="var(--text-2)">
                        (×{d.extra + 1} → keep 1)
                      </Typography>
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid var(--border-1)", gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={applying}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontFamily: "Poppins", borderColor: "var(--border-1)", color: "var(--text-1)" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          disabled={applying || noChange}
          variant="contained"
          size="small"
          startIcon={applying ? <CircularProgress size={13} color="inherit" /> : <CheckCircleOutline sx={{ fontSize: 15 }} />}
          sx={{ textTransform: "none", fontFamily: "Poppins" }}
        >
          {applying ? "Applying…" : isRestore ? "Approve & restore" : "Approve & combine"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeReviewDialog;
